<script setup lang="ts">
import { useOnline } from '@vueuse/core'
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { queryClient } from '~/app/query-client'
import { useUiStore } from '~/stores/ui'

/**
 * Offline detection (frontend phase-13 §6).
 *
 * TanStack Query already pauses fetches and mutations while the browser reports no network;
 * this makes that visible, and on reconnect resumes the paused mutations and refreshes what is
 * on screen. The banner is a live region so a screen-reader user hears the change too.
 */
const { t } = useI18n()
const online = useOnline()
const ui = useUiStore()

watch(online, (isOnline, wasOnline) => {
  if (isOnline && wasOnline === false) {
    ui.notify('success', t('platform.backOnline'))
    void queryClient.resumePausedMutations().then(() => queryClient.invalidateQueries())
  }
})
</script>

<template>
  <div v-if="!online" class="apmp-offline" role="status" aria-live="polite" data-testid="offline-banner">
    {{ t('platform.offline') }}
  </div>
</template>

<style scoped>
.apmp-offline {
  position: sticky;
  top: 0;
  z-index: 2000;
  padding: 10px 16px;
  text-align: center;
  font-size: 14px;
  background: var(--apmp-warning-bg, #fff7e6);
  color: var(--apmp-warning-text, #7a4b00);
  border-bottom: 1px solid var(--apmp-warning-border, #ffd591);
}
</style>
